import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUserAuth } from "../../context/userAuthContext";
import { db, storage } from "../../firebase";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  deleteDoc,
  updateDoc,
  serverTimestamp,
  query,
  orderBy,
  where
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  Plus,
  Trash2,
  Edit,
  Save,
  X,
  Loader2,
  Image,
  Tag,
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronUp,
  MoreVertical,
  Search
} from "lucide-react";

const NewCategory = () => {
  const navigate = useNavigate();
  const { user } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // For new categories
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    parentCategory: "",
    description: "",
    icon: null,
    iconUrl: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch categories from Firestore
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const categoriesCollection = collection(db, "vineCategories");
        const q = query(categoriesCollection, orderBy("name", "asc"));
        const querySnapshot = await getDocs(q);

        const categoryList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          subcategories: []
        }));

        // Process categories to organize into hierarchy
        const rootCategories = categoryList.filter(cat => !cat.parentCategory);
        const subCategories = categoryList.filter(cat => cat.parentCategory);

        // Assign subcategories to their parents
        subCategories.forEach(subCat => {
          const parentCategory = rootCategories.find(cat => cat.id === subCat.parentCategory);
          if (parentCategory) {
            if (!parentCategory.subcategories) {
              parentCategory.subcategories = [];
            }
            parentCategory.subcategories.push(subCat);
          }
        });

        setCategories(rootCategories);
      } catch (error) {
        console.error("Error fetching categories:", error);
        setError("Error al cargar categorías");
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const handleIconChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type and size
      const validTypes = ['image/jpeg', 'image/png', 'image/svg+xml', 'image/gif'];
      const maxSize = 2 * 1024 * 1024; // 2MB

      if (!validTypes.includes(file.type)) {
        setError("Formato de imagen no válido. Usa JPG, PNG, SVG o GIF.");
        return;
      }

      if (file.size > maxSize) {
        setError("El archivo es demasiado grande. El tamaño máximo es 2MB.");
        return;
      }

      setFormData((prev) => ({
        ...prev,
        icon: file,
        iconUrl: URL.createObjectURL(file)
      }));

      setError("");
    }
  };

  const uploadIcon = async (file) => {
    if (!file) return null;

    setUploadingIcon(true);
    try {
      const storageRef = ref(storage, `categoryIcons/${user.uid}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      setUploadingIcon(false);
      return downloadURL;
    } catch (error) {
      console.error("Error uploading icon:", error);
      setError("Error al subir el icono. Inténtalo de nuevo.");
      setUploadingIcon(false);
      throw error;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      name: "",
      parentCategory: "",
      description: "",
      icon: null,
      iconUrl: ""
    });
    setIsEditing(false);
    setSelectedCategory(null);
    setShowForm(false);
    setError("");
    setSuccess("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError("El nombre de la categoría es obligatorio");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Check if category with same name already exists
      if (!isEditing) {
        const categoriesCollection = collection(db, "vineCategories");
        const q = query(categoriesCollection, where("name", "==", formData.name.trim()));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          setError("Ya existe una categoría con este nombre");
          setLoading(false);
          return;
        }
      }

      let iconUrl = formData.iconUrl;

      // Upload icon if it's a file
      if (formData.icon && formData.icon instanceof File) {
        iconUrl = await uploadIcon(formData.icon);
      }

      if (isEditing && selectedCategory) {
        // Update existing category
        const categoryRef = doc(db, "vineCategories", selectedCategory.id);
        await updateDoc(categoryRef, {
          name: formData.name.trim(),
          description: formData.description.trim(),
          parentCategory: formData.parentCategory || null,
          iconUrl: iconUrl || null,
          updatedAt: serverTimestamp(),
          updatedBy: user.uid
        });

        setSuccess("Categoría actualizada correctamente");
      } else {
        // Create new category
        const categoriesCollection = collection(db, "vineCategories");
        await addDoc(categoriesCollection, {
          name: formData.name.trim(),
          description: formData.description.trim(),
          parentCategory: formData.parentCategory || null,
          iconUrl: iconUrl || null,
          createdAt: serverTimestamp(),
          createdBy: user.uid,
          updatedAt: serverTimestamp(),
          updatedBy: user.uid
        });

        setSuccess("Categoría creada correctamente");
      }

      // Refresh categories
      const categoriesCollection = collection(db, "vineCategories");
      const q = query(categoriesCollection, orderBy("name", "asc"));
      const querySnapshot = await getDocs(q);

      const categoryList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        subcategories: []
      }));

      // Process categories to organize into hierarchy
      const rootCategories = categoryList.filter(cat => !cat.parentCategory);
      const subCategories = categoryList.filter(cat => cat.parentCategory);

      // Assign subcategories to their parents
      subCategories.forEach(subCat => {
        const parentCategory = rootCategories.find(cat => cat.id === subCat.parentCategory);
        if (parentCategory) {
          if (!parentCategory.subcategories) {
            parentCategory.subcategories = [];
          }
          parentCategory.subcategories.push(subCat);
        }
      });

      setCategories(rootCategories);
      resetForm();
    } catch (error) {
      console.error("Error saving category:", error);
      setError("Error al guardar la categoría. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category) => {
    setSelectedCategory(category);
    setFormData({
      name: category.name || "",
      parentCategory: category.parentCategory || "",
      description: category.description || "",
      icon: null,
      iconUrl: category.iconUrl || ""
    });
    setIsEditing(true);
    setShowForm(true);
    setError("");
    setSuccess("");
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm("¿Estás seguro de eliminar esta categoría? Esta acción no se puede deshacer.")) {
      return;
    }

    setLoading(true);

    try {
      // Check if there are subcategories
      const category = categories.find(cat => cat.id === categoryId) ||
        categories.flatMap(cat => cat.subcategories || []).find(subcat => subcat.id === categoryId);

      if (category && category.subcategories && category.subcategories.length > 0) {
        setError("No se puede eliminar una categoría con subcategorías. Elimina primero las subcategorías.");
        setLoading(false);
        return;
      }

      // TODO: Check if the category is used in any products/restaurants
      // This would require checking other collections

      // Delete the category
      await deleteDoc(doc(db, "vineCategories", categoryId));
      setSuccess("Categoría eliminada correctamente");

      // Update the UI
      setCategories(prevCategories => {
        // Check if it's a root category
        const newCategories = prevCategories.filter(cat => cat.id !== categoryId);

        // If not in root, check subcategories
        return newCategories.map(cat => {
          if (cat.subcategories && cat.subcategories.length > 0) {
            return {
              ...cat,
              subcategories: cat.subcategories.filter(subcat => subcat.id !== categoryId)
            };
          }
          return cat;
        });
      });
    } catch (error) {
      console.error("Error deleting category:", error);
      setError("Error al eliminar la categoría. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  // Filter categories based on search term
  const filteredCategories = categories.filter(category => {
    const matchesSearch = category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (category.description && category.description.toLowerCase().includes(searchTerm.toLowerCase()));

    // Check subcategories too
    const hasMatchingSubcategories = category.subcategories &&
      category.subcategories.some(subcat =>
        subcat.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (subcat.description && subcat.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );

    return matchesSearch || hasMatchingSubcategories;
  });

  // Category component for rendering each category
  const CategoryItem = ({ category, isSubcategory = false }) => {
    const [expanded, setExpanded] = useState(false);
    const hasSubcategories = category.subcategories && category.subcategories.length > 0;

    return (
      <div className={`border rounded-lg overflow-hidden mb-2 ${isSubcategory ? "ml-6" : ""}`}>
        <div className="flex items-center justify-between p-3 bg-white hover:bg-gray-50">
          <div className="flex items-center flex-1">
            {hasSubcategories && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="mr-2 p-1 rounded-full hover:bg-gray-200"
              >
                {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
            )}

            {category.iconUrl ? (
              <img
                src={category.iconUrl}
                alt={category.name}
                className="w-8 h-8 mr-3 object-contain"
              />
            ) : (
              <Tag className="w-6 h-6 mr-3 text-gray-400" />
            )}

            <div>
              <h3 className="font-medium text-gray-900">{category.name}</h3>
              {category.description && (
                <p className="text-sm text-gray-500">{category.description}</p>
              )}
            </div>
          </div>

          <div className="flex items-center">
            <button
              onClick={() => handleEdit(category)}
              className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-full"
              title="Editar"
            >
              <Edit size={16} />
            </button>
            <button
              onClick={() => handleDelete(category.id)}
              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-full"
              title="Eliminar"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>

        {hasSubcategories && expanded && (
          <div className="pl-2 pr-2 pb-2 pt-0 bg-gray-50 border-t">
            {category.subcategories.map(subcat => (
              <CategoryItem
                key={subcat.id}
                category={subcat}
                isSubcategory={true}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <button
              onClick={() => navigate(-1)}
              className="mr-4 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Gestión de Categorías
              </h1>
              <p className="text-sm text-gray-600">
                Administra las categorías y subcategorías para tus restaurantes y productos
              </p>
            </div>
          </div>

          <button
            onClick={() => {
              resetForm();
              setShowForm(!showForm);
            }}
            className={`px-4 py-2 rounded-lg flex items-center font-medium ${showForm
                ? "bg-gray-200 text-gray-800 hover:bg-gray-300"
                : "bg-purple-600 text-white hover:bg-purple-700"
              }`}
          >
            {showForm ? (
              <>
                <X size={18} className="mr-1" />
                Cancelar
              </>
            ) : (
              <>
                <Plus size={18} className="mr-1" />
                Nueva Categoría
              </>
            )}
          </button>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded">
            <div className="flex">
              <div className="flex-shrink-0">
                <X className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 border-l-4 border-green-500 p-4 rounded">
            <div className="flex">
              <div className="flex-shrink-0">
                <Check className="h-5 w-5 text-green-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-700">{success}</p>
              </div>
            </div>
          </div>
        )}

        {/* Create/Edit Category Form */}
        {showForm && (
          <div className="bg-white shadow rounded-lg mb-6 p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              {isEditing ? "Editar Categoría" : "Nueva Categoría"}
            </h2>

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {/* Category Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                    placeholder="Nombre de la categoría"
                  />
                </div>

                {/* Parent Category */}
                <div>
                  <label htmlFor="parentCategory" className="block text-sm font-medium text-gray-700 mb-1">
                    Categoría Padre
                  </label>
                  <select
                    id="parentCategory"
                    name="parentCategory"
                    value={formData.parentCategory}
                    onChange={handleInputChange}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                  >
                    <option value="">Ninguna (Categoría principal)</option>
                    {categories.map(category => (
                      <option
                        key={category.id}
                        value={category.id}
                        disabled={isEditing && selectedCategory && selectedCategory.id === category.id}
                      >
                        {category.name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Si seleccionas una categoría padre, esta será una subcategoría
                  </p>
                </div>
              </div>

              {/* Description */}
              <div className="mb-4">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                  placeholder="Describe brevemente esta categoría"
                />
              </div>

              {/* Icon Upload */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Icono de Categoría
                </label>

                <div className="flex items-start space-x-4">
                  <div className="w-20 h-20 flex-shrink-0 bg-gray-100 rounded-md flex items-center justify-center border border-gray-300">
                    {formData.iconUrl ? (
                      <img
                        src={formData.iconUrl}
                        alt="Category icon"
                        className="w-full h-full object-contain rounded-md"
                      />
                    ) : (
                      <Image className="w-8 h-8 text-gray-400" />
                    )}
                  </div>

                  <div className="flex-1">
                    <label
                      htmlFor="icon"
                      className="relative flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer"
                    >
                      <span>Seleccionar Archivo</span>
                      <input
                        id="icon"
                        name="icon"
                        type="file"
                        accept="image/*"
                        onChange={handleIconChange}
                        className="sr-only"
                      />
                    </label>
                    <p className="mt-1 text-xs text-gray-500">
                      PNG, JPG, GIF o SVG. Máximo 2MB.
                    </p>
                    {formData.iconUrl && (
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, icon: null, iconUrl: "" }))}
                        className="mt-2 text-sm text-red-600 hover:text-red-800"
                      >
                        Eliminar icono
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading || uploadingIcon}
                  className="inline-flex justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                >
                  {loading || uploadingIcon ? (
                    <>
                      <Loader2 className="animate-spin mr-2 h-4 w-4" />
                      {isEditing ? "Actualizando..." : "Creando..."}
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      {isEditing ? "Actualizar" : "Crear"} Categoría
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              placeholder="Buscar categorías..."
            />
          </div>
        </div>

        {/* Categories List */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Lista de Categorías
            </h2>

            {loading && categories.length === 0 ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 text-purple-500 animate-spin" />
              </div>
            ) : filteredCategories.length > 0 ? (
              <div className="space-y-2">
                {filteredCategories.map(category => (
                  <CategoryItem key={category.id} category={category} />
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-gray-500">
                {searchTerm ? (
                  <>
                    <p className="mb-1">No se encontraron categorías que coincidan con "{searchTerm}"</p>
                    <button
                      onClick={() => setSearchTerm("")}
                      className="text-purple-600 hover:text-purple-800"
                    >
                      Limpiar búsqueda
                    </button>
                  </>
                ) : (
                  <p>No hay categorías creadas. Crea tu primera categoría usando el botón "Nueva Categoría".</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-8 bg-blue-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-800 mb-2">
            Consejos para crear categorías
          </h3>
          <ul className="space-y-1 text-sm text-blue-700 ml-5 list-disc">
            <li>Usa nombres claros y descriptivos para tus categorías</li>
            <li>Crea primero las categorías principales y luego las subcategorías</li>
            <li>Los iconos ayudan a los usuarios a identificar fácilmente las categorías</li>
            <li>Evita crear demasiadas categorías principales, es mejor organizarlas en subcategorías</li>
            <li>Las categorías se mostrarán en menús y filtros de búsqueda</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default NewCategory;